import type { Schedule } from "@/pages/human-resources/time-management/business/schedule/Schedule.ts";
import { partition } from "@/shared/utils/Arrays.ts";
import { LEGACY_TIME_ZONE } from "@/shared/utils/LegacyDates.ts";
import { DateTime } from "luxon";
import { immerable } from "immer";
import type { Employee } from "@/pages/human-resources/time-management/business/Employee.ts";
import { EmployeeDaySchedule } from "@/pages/human-resources/time-management/business/EmployeeDaySchedule.ts";
import type { WorkDurationType } from "./WorkDurationType.ts";

export class EmployeeSchedules {
  [immerable] = true;

  public readonly employee: Employee;
  private readonly onMainSite: Map<string, Schedule>;
  private readonly onOtherSites: Map<string, Schedule[]>;

  constructor(employee: Employee, schedules: Schedule[]) {
    this.employee = employee;
    this.onMainSite = new Map<string, Schedule>();
    this.onOtherSites = new Map<string, Schedule[]>();
    const [mainSchedules, delegatedSchedules] = partition(
      schedules,
      (s) => s.otherSiteId === undefined,
    );

    for (const schedule of mainSchedules) {
      const dateKey = keyForDate(schedule.date);
      const previous = this.onMainSite.get(dateKey);
      if (previous === undefined) {
        this.onMainSite.set(dateKey, schedule);
      } else {
        throw new Error(
          `Employee ${this.employee.id} has duplicate schedules on ${dateKey}: ${schedule.id} and ${previous.id}`,
        );
      }
    }

    for (const schedule of delegatedSchedules) {
      const dateKey = keyForDate(schedule.date);
      const previous = this.onOtherSites.get(dateKey);
      if (previous === undefined) {
        this.onOtherSites.set(dateKey, [schedule]);
      } else {
        const conflicting = previous.find(
          (s) => s.otherSiteId === schedule.otherSiteId,
        );
        if (conflicting === undefined) {
          previous.push(schedule);
        } else {
          throw new Error(
            `Employee ${this.employee.id} has duplicate schedules on ${dateKey}: ${schedule.id} and ${conflicting.id}`,
          );
        }
      }
    }
  }

  public hasModifications(): boolean {
    return this.getMainSchedules().some((s) => s.modified);
  }

  public getMainSchedules(): Schedule[] {
    return [...this.onMainSite.values()];
  }

  public getMainSchedule(date: Date): Schedule | undefined {
    return this.onMainSite.get(keyForDate(date));
  }

  public getOtherSitesSchedules(date: Date): Schedule[] {
    return this.onOtherSites.get(keyForDate(date)) ?? [];
  }

  public asEmployeeSchedule(date: Date): EmployeeDaySchedule {
    return new EmployeeDaySchedule(this, date);
  }

  public getOrComputeWorkDurationAtDate(
    date: Date,
    workDurationType: WorkDurationType,
  ): number {
    return this.getOrComputeScheduleWorkDuration(
      this.getMainSchedule(date),
      workDurationType,
    );
  }

  public getOrComputeScheduleWorkDuration(
    schedule: Schedule | undefined,
    workDurationType: WorkDurationType,
  ): number {
    if (schedule === undefined) return 0;
    switch (workDurationType) {
      case "VALORIZED": {
        return schedule.valorizedWorkMinutes;
      }
      case "EFFECTIVE": {
        const contract = this.employee.mainSiteContractAtDate(schedule.date);
        return schedule.computeWorkDurationMinutes(contract);
      }
    }
  }
}

function keyForDate(date: Date): string {
  return DateTime.fromJSDate(date, { zone: LEGACY_TIME_ZONE }).toFormat(
    "yyyy-MM-dd",
  );
}
